import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { OrderInfoSchema } from "@/components/order/OrderSchema";
import React from "react";
import { Dropdown } from "@/components/ui/select";
import { getCalendarWeek, setMonthDays } from "@/helper/sort";
import { useNavigate } from "react-router-dom";
import { useApiFetch } from "@/helper/apiFetch";
import { useTranslate } from "@/helper/translate";

type Lookup = { id: string; name: string, color: string, loopLength?: number };



// Component
export function OccupancyPage() {
  const apiFetch = useApiFetch();
  const { translate, formatMonthYear } = useTranslate();

  const now = new Date();
  const initialDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const daysOfWeek = ["KW", "Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const [orders, setOrders] = useState<OrderInfoSchema[]>([]);
  //const [bookings, setBookings] = useState<BookingInfoSchema[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [sites, setSites] = useState<Lookup[]>([]);

  const [isLoaded, setIsLoaded] = useState(false);



  const handleSiteChange = (siteId: string) => {
 
    setSelectedSite(siteId);

  }

  function getOrderLength(date: Date): number {
    const length = orders.filter(s => new Date(s.orderBegin) <= date && new Date(s.orderEnd) >= date)
      .reduce((sum, s) => sum + (s.numberOfLoops * s.orderLength), 0);

    return length;

  }

  function getBookingLength(date: Date, orderId?: string): number {
    let l = 0;
    orders.filter(s => s.orderId == (orderId ?? s.orderId) && new Date(s.orderBegin) <= date && new Date(s.orderEnd) >= date).map(order => {
      const bookings = order.bookings.filter(s => new Date(s.bookingBegin) <= date && new Date(s.bookingEnd) >= date);
      if (order.rotation == "S")
        l += bookings.length > 0 ? bookings[0].length : 0;
      else if (order.rotation == "M" && bookings.length == 1)
        l += bookings[0].length * 2;
      else if (order.rotation == "M" && bookings.length >= 2)//should never be more than 2
        l += bookings[0].length + bookings[1].length;
      else if (order.rotation == "L" && bookings.length == 1)
        l += bookings[0].length * 3;
      else if (order.rotation == "L" && bookings.length >= 3)//should never be more than 3
        l += bookings[0].length + bookings[1].length + bookings[2].length;
      else if (order.rotation == "L" && bookings.length == 2)
        l += (bookings[0].length + Math.round(bookings[1].length) / 2.0 * 3);//avg
    });


    return l;

  }


  const backward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);
    setCurrentDate(newDate);
    setDays();
  };

  const forward = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
    setDays();
  };


  const siteLoopLength = (sites.find(s => s.id == selectedSite)?.loopLength ?? 0) * 3;
  const rows = [];
  let days = [];

  const navigate = useNavigate();
  const openPlaylist = useCallback((day: Date) => {
    const params = new URLSearchParams({
      siteId: selectedSite ?? '',
      date: day.toString(),
    });
    navigate(`/playlists?${params.toString()}`);
  }, [navigate, selectedSite]);


  function setDays() {
    days = setMonthDays(currentDate);
    for (let i = 0; i < days.length; i += 7) {
      const weekDays = days.slice(i, i + 7);
      const weekNumber = getCalendarWeek(weekDays[0]);

      rows.push({ weekNumber, days: weekDays });
    }
  }
  setDays();
  useEffect(() => {

    apiFetch(`/App/GetSiteLookup`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch sites");
        return res.json();
      })
      .then((data) => {
        setSites(data);
        setSelectedSite(data[0].id);
      })
      .catch((err) => {
        console.error(err);
      });


  }, []);





  useEffect(() => {
    {
      if (selectedSite && selectedSite != "") {
        setIsLoaded(false);
        apiFetch(`/App/GetPlaylist?siteId=${selectedSite}`)
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch playlist");
            return res.json();
          })
          .then((data) => {
            setOrders(data.orders);
            setIsLoaded(true);


          })
          .catch((err) => {
            console.error(err);
          });

      }
    }
  }, [selectedSite]);


  return (
    <div className="h-[calc(100vh-60px)] flex flex-col">
      <div className="content-header flex justify-between pt-[0.6rem] pb-[0.6rem] px-8 bg-gray-100 dark:bg-zinc-900">
        <div className="flex items-center gap-4">
          <h1 className="font-bold text-2xl">{translate("ui.occupancy")}</h1>

          <Dropdown
            className="w-[20rem]"
            source={sites}
            value={selectedSite}
            valueMember={"id"}
            displayMember={"name"}
            onChange={(value) => handleSiteChange(value as string)}
          />



        </div>
      </div>



      {!isLoaded ? (
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
      ) : (

        <div className="flex-1 overflow-auto px-10">
          <div className="pb-5 mt-3">


            <div className="calendarWrapper">
              <div className="occupancyHeader">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div className="calendarNav left text-sky-600 dark:text-orange-400" onClick={backward}><ChevronLeft /></div>
                  <div className="w-[280px] uppercase tracking-[2px] font-light select-none" style={{ textAlign: "center", fontSize: "20px", cursor: "pointer" }}>{formatMonthYear(currentDate)}</div>
                  <div className="calendarNav right text-sky-600 dark:text-orange-400" onClick={forward}><ChevronRight /></div>
                </div>
                <div></div>
              </div>

              <div className="overflow-x-auto mt-2 mb-10 rounded-xl shadow-2xl">
                <table className="calendarTable border-[0] border-collapse select-none">
                  <colgroup>
                    <col style={{ width: "2%" }} />
                    {Array.from({ length: 7 }).map((_, idx) => (
                      <col key={idx} style={{ width: "14%" }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {daysOfWeek.map((day, idx) => (
                        <td key={idx} className="calendarItem itemHeader text-sm bg-gray-200 text-gray-600 dark:bg-zinc-800 dark:text-zinc-200">{day}</td>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[0.8rem]">
                    {rows.map((row, rIdx) => (

                      <React.Fragment key={rIdx}>
                        <tr>
                          <td className="calendarItem calendarKW bg-gray-200 dark:bg-zinc-800 text-center font-bold text-base">{row.weekNumber}</td>
                          {row.days.map((day, dIdx) => (
                            <td
                              key={dIdx}
                              className={day.toDateString() === new Date().toDateString()
                                ? "calendarItem today calendarDay cursor-pointer dark:bg-zinc-700"
                                : "calendarItem calendarDay cursor-pointer dark:bg-zinc-700"}
                              onClick={() => { openPlaylist(day) }}
                            >
                              <div className="calendarDayHeader flex justify-end">
                                <div className="flex items-center justify-center mb-2 text-base font-semibold text-gray-700 dark:text-zinc-200 w-[24px] h-[24px] rounded-full bg-transparent">{day.getDate()}</div>
                              </div>
                              <div className="w-100 bg-slate-200 dark:bg-zinc-500 rounded-sm pl-1">
                                {translate("ui.site")}: {siteLoopLength}s
                              </div>
                              <div className="w-100 relative">
                                <div className="relative z-10 pl-1">
                                  {translate("ui.orders")}: {getOrderLength(day)}s / {Math.round(getOrderLength(day) / siteLoopLength * 100)}%
                                </div>
                                <div className="bg-slate-200 dark:bg-zinc-500 rounded-sm absolute top-0 left-0"

                                  style={{ width: `${(getOrderLength(day) / siteLoopLength) * 100}%`, height: "100%" }}

                                >

                                </div>
                              </div>

                              <div className="w-100 relative">
                                <div className="relative z-10 pl-1">
                                  {translate("ui.bookings")}: {Math.round(getBookingLength(day))}s / {Math.round(getBookingLength(day) / siteLoopLength * 100)}%
                                </div>
                                <div className="bg-slate-200 dark:bg-zinc-500 rounded-sm absolute top-0 left-0"

                                  style={{ width: `${(getBookingLength(day) / siteLoopLength) * 100}%`, height: "100%" }}

                                >

                                </div>
                              </div>


                            </td>
                          ))}

                        </tr>

                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

            </div>

          </div>
        </div>)}

    </div>
  );
}
